import * as THREE from 'three'

export type FighterPoseSample={
  bones:number
  width:number
  height:number
  depth:number
  diagonal:number
}
export type FighterPoseIntegrityReport={
  baseline:FighterPoseSample
  current:FighterPoseSample
  heightRatio:number
  diagonalRatio:number
  boneRatio:number
  green:boolean
}
export type FighterBonePose={bone:THREE.Bone;position:THREE.Vector3;quaternion:THREE.Quaternion;scale:THREE.Vector3}
export type FighterPoseSnapshot={entries:FighterBonePose[];captures:number;restores:number}

function finite(value:number){return Number.isFinite(value)}
function safeRatio(value:number,baseline:number){return baseline>1e-7&&finite(value)?value/baseline:0}

export function sampleFighterPose(root:THREE.Object3D):FighterPoseSample{
  root.updateMatrixWorld(true)
  const inverse=root.matrixWorld.clone().invert()
  const box=new THREE.Box3()
  const point=new THREE.Vector3()
  let bones=0
  root.traverse(node=>{
    if(!(node as THREE.Bone).isBone)return
    point.setFromMatrixPosition(node.matrixWorld).applyMatrix4(inverse)
    if(![point.x,point.y,point.z].every(finite))return
    box.expandByPoint(point)
    bones++
  })
  if(bones<1||box.isEmpty())return{bones,width:0,height:0,depth:0,diagonal:0}
  const size=box.getSize(new THREE.Vector3())
  return{bones,width:size.x,height:size.y,depth:size.z,diagonal:size.length()}
}

export function captureFighterPoseSnapshot(root:THREE.Object3D):FighterPoseSnapshot{
  const entries:FighterBonePose[]=[]
  root.traverse(node=>{
    if(!(node as THREE.Bone).isBone)return
    const bone=node as THREE.Bone
    entries.push({bone,position:bone.position.clone(),quaternion:bone.quaternion.clone(),scale:bone.scale.clone()})
  })
  return{entries,captures:1,restores:0}
}

export function refreshFighterPoseSnapshot(snapshot:FighterPoseSnapshot){
  for(const entry of snapshot.entries){
    entry.position.copy(entry.bone.position)
    entry.quaternion.copy(entry.bone.quaternion)
    entry.scale.copy(entry.bone.scale)
  }
  snapshot.captures++
  return snapshot.entries.length
}

export function restoreFighterPoseSnapshot(root:THREE.Object3D,snapshot:FighterPoseSnapshot){
  for(const entry of snapshot.entries){
    entry.bone.position.copy(entry.position)
    entry.bone.quaternion.copy(entry.quaternion)
    entry.bone.scale.copy(entry.scale)
    entry.bone.updateMatrix()
  }
  root.updateMatrixWorld(true)
  root.traverse(node=>{
    if(node instanceof THREE.SkinnedMesh)try{node.skeleton.update()}catch{}
  })
  snapshot.restores++
  return snapshot.entries.length
}

export function assessFighterPoseIntegrity(
  root:THREE.Object3D,
  baseline:FighterPoseSample,
  minHeightRatio=.42,
  maxHeightRatio=1.7,
  minDiagonalRatio=.55,
  maxDiagonalRatio=1.65,
):FighterPoseIntegrityReport{
  const current=sampleFighterPose(root)
  const heightRatio=safeRatio(current.height,baseline.height)
  const diagonalRatio=safeRatio(current.diagonal,baseline.diagonal)
  const boneRatio=baseline.bones>0?current.bones/baseline.bones:0
  const green=
    baseline.bones>=20&&
    boneRatio===1&&
    heightRatio>=minHeightRatio&&heightRatio<=maxHeightRatio&&
    diagonalRatio>=minDiagonalRatio&&diagonalRatio<=maxDiagonalRatio
  return{baseline,current,heightRatio,diagonalRatio,boneRatio,green}
}
